import dynamic from 'next/dynamic'
import { Fragment } from 'react'
import { Pagination } from '~/components/modules/Pagination/Pagination'
import { useBrandByBrandId } from '~/features/shared/brand/hooks/useBrandByBrandId'
import { useModal } from '~/hooks/useModal'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { ITEM_LIST_FAVORITE_BRAND_ADJUST_URL } from '~/lib/constants'
import { ElementId } from '~/types/imobileAd'
import { BrandInformation } from '../shared/search/components/BrandInformation'
import { ScrollFollower } from '../shared/search/components/ScrollFollower'
import { SearchResultFooter } from '../shared/search/components/SearchResultFooter'
import { SearchResultHeader } from '../shared/search/components/SearchResultHeader'
import { ShopFilterPanel } from '../shared/search/components/pc/FilterPanel/ShopFilterPanel'
import { SortSelect } from '../shared/search/components/pc/SortSelect'
import { buildUrl } from '../shared/search/coordinate/builder/buildUrl'
import { useNotFoundGtmEvent } from '../shared/search/hooks/useNotFoundGtmEvent'
import { useSearchShops } from '../shared/search/hooks/useSearchShops'
import { buildItemListUrl } from '../shared/search/item/buildItemListUrl'
import { buildShopListUrl } from '../shared/search/shop/buildShopListUrl'
import { NormalizedConditions } from '../shared/search/shop/types'
import { countFilteredConditions } from '../shared/search/shop/utils/countFilteredConditions'
import { buildUserListUrl } from '../shared/search/user/buildUserListUrl'
import { ShopCard } from './components/ShopCard'
import {
  headerAd,
  leftNavAd,
  pageBottomPcAds,
  inListPcAds,
  inListSpAds,
  pageBottomSpAds,
} from './data/googleAd'
import { convertNormalizedConditionsToParameters } from './lib/convertNormalizedConditionsToParameters'
import { getTitle } from './lib/getTitle'

const FilterDialog = dynamic(
  () => import('./sp/FilterDialog').then((mod) => mod.FilterDialog),
  {
    ssr: false,
  }
)

const SpThinBanner = dynamic(
  () => import('~/features/shared/banner/SP/ThinBanner'),
  { ssr: false }
)

const PcThinBanner = dynamic(
  () =>
    import('~/features/shared/banner/PC/ThinBanner').then(
      (mod) => mod.ThinBanner
    ),
  { ssr: false }
)

const GoogleAd = dynamic(
  () => import('~/features/shared/ad/components/GoogleAd'),
  { ssr: false }
)

const pcListAd = new Map(
  inListPcAds.map(({ afterRows, ad }) => [afterRows, ad])
)

const spListAd = new Map<number, ElementId>(
  inListSpAds.map(({ afterRows, ad }) => [afterRows, ad])
)

type Props = {
  conditions: NormalizedConditions
  pageNumber: number
}

export const FETCH_COUNT_PER_PAGE = 60

// eslint-disable-next-line complexity
export const ShopList: React.FC<Props> = ({ conditions, pageNumber }) => {
  const { searchWord, brand, sort } = conditions

  const [FilterModal, openFilterModal, closeFilterModal, filterModalIsOpen] =
    useModal(300)

  const parameter = convertNormalizedConditionsToParameters(conditions, {
    pagesize: FETCH_COUNT_PER_PAGE,
  })
  const { data: searchResult } = useSearchShops(parameter)
  if (!searchResult)
    throw new Error('Unexpected error: searchResult is undefined')

  const totalPage = Math.ceil(
    Math.min(searchResult.total_count, searchResult.max_count) /
      FETCH_COUNT_PER_PAGE
  )
  const paginationProps = usePaginationProps(pageNumber, totalPage, false)

  useNotFoundGtmEvent('shop', searchResult.total_count)

  const title = getTitle({ ...conditions, brandName: conditions.brand?.name })
  const { data: searchBrandRes } = useBrandByBrandId(
    brand ? { brand_id: brand.id } : undefined
  )
  const searchBrand = searchBrandRes?.brand

  return (
    <div className="flex flex-col items-center">
      <div className="hidden w-full xl:block">
        <PcThinBanner bannerType="pc_gbl_ad_other" />
      </div>
      <div className="w-full xl:hidden">
        <SpThinBanner bannerType="sp_narrow_menu_other" />
      </div>
      <SearchResultHeader
        conditions={conditions}
        title={{ main: title }}
        showSpTopTitle={conditions.searchWord === undefined}
        sortTypes={(
          [
            { name: '人気順', value: 'popular' },
            { name: 'A-Z順', value: 'alphabetically' },
          ] as const
        ).map((item) => ({
          ...item,
          href:
            sort === item.value
              ? undefined
              : buildShopListUrl({
                  ...conditions,
                  sort: item.value,
                }),
        }))}
        filteredCount={countFilteredConditions(conditions)}
        onFilterModalOpenClick={openFilterModal}
        currentSearchTab="shop"
        searchTabLinks={{
          coordinate: buildUrl({
            sort: 'popular',
            searchWord,
            snapItems: brand ? [{ brand }] : undefined,
          }),
          item: buildItemListUrl({
            sort: 'popular',
            searchWord,
            brand,
          }),
          user: buildUserListUrl({
            sort: 'popular',
            searchWord,
          }),
          shop: buildShopListUrl(conditions),
        }}
        resultCount={searchResult.total_count.toLocaleString()}
        pcHeaderAd={<GoogleAd elementId={headerAd} />}
        hideGenderTabs
      />
      <FilterModal
        overlayClassName={`bg-black/50 transition-opacity ease-out duration-300 ${
          filterModalIsOpen ? 'opacity-100' : 'opacity-0'
        }`}
        className={`size-full overflow-y-auto overflow-x-hidden bg-gray-50 transition-transform duration-300 ease-out ${
          filterModalIsOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <FilterDialog
          normalizedConditions={conditions}
          onBackClick={closeFilterModal}
        />
      </FilterModal>
      <main className="grid w-full max-w-[990px] grid-cols-1 gap-[18px] xl:grid-cols-[234px_minmax(0,1fr)] xl:pt-[18px]">
        <div className="sticky top-[65px] hidden h-fit xl:block">
          <ScrollFollower
            marginTop={64}
            className="z-10 flex flex-col gap-[30px]"
          >
            <ShopFilterPanel conditions={conditions} />
            <GoogleAd elementId={leftNavAd} />
          </ScrollFollower>
        </div>
        <div className="flex flex-col">
          <div className="z-10 hidden self-end px-[10px] pb-[18px] xl:block">
            <SortSelect
              items={[
                {
                  value: 'popular',
                  name: '人気順',
                  href: buildShopListUrl({
                    ...conditions,
                    sort: 'popular',
                  }),
                },
                {
                  value: 'alphabetically',
                  name: 'A-Z順',
                  href: buildShopListUrl({
                    ...conditions,
                    sort: 'alphabetically',
                  }),
                },
              ]}
              value={sort ?? 'popular'}
            />
          </div>
          <section>
            <h2 className="sr-only">店舗一覧</h2>
            <ul className="flex flex-col xl:gap-y-[18px]">
              {searchResult.shops.map((shop, i) => {
                const pcAds = pcListAd.get(i)
                const spAd = spListAd.get(i)
                const brands = shop.brands
                  .map((brand) => {
                    if (brand === null || brand.key === null) return undefined
                    return {
                      name: brand.name,
                      key: brand.key,
                      id: brand.id,
                    }
                  })
                  .filter((brand) => brand !== undefined)
                return (
                  <Fragment key={shop.id}>
                    {pcAds !== undefined && (
                      <li className="col-span-full hidden justify-between px-12 py-[22px] xl:flex">
                        <GoogleAd elementId={pcAds[0]} />
                        <GoogleAd elementId={pcAds[1]} />
                      </li>
                    )}
                    {spAd !== undefined && (
                      <li className="col-span-full flex justify-center bg-gray-50 px-[10px] py-[35px] xl:hidden">
                        <div className="min-h-[250px] min-w-[300px] bg-white">
                          <GoogleAd elementId={spAd} />
                        </div>
                      </li>
                    )}
                    <li>
                      <ShopCard
                        id={shop.id}
                        name={shop.name}
                        address={shop.address}
                        zipcode={shop.zipcode}
                        imageUrl={shop.image.url_80 ?? undefined}
                        coordinatesCount={
                          shop.shop_aggregate?.coordinates_count ?? 0
                        }
                        brands={brands}
                      />
                    </li>
                  </Fragment>
                )
              })}
            </ul>
          </section>
          {searchResult.shops.length === 0 && (
            <p className="w-full px-[10px] pb-[29px] pt-[45px] text-center text-[16px] xl:pb-[146px] xl:pt-[123px] xl:text-[20px]">
              この検索結果はありません
            </p>
          )}
          <h1 className="px-[10px] pt-[17px] text-center leading-[1.4] xl:hidden">
            {title}
          </h1>
          {searchResult.shops.length !== 0 && (
            <div className="pt-4 xl:pt-[31px]">
              <Pagination {...paginationProps} />
            </div>
          )}
          <div className="pt-[18px] xl:pt-[41px]">
            <SearchResultFooter
              brand={brand}
              showSearchViaZozotownButton={brand !== undefined}
              pcFooterAd={
                <>
                  {pageBottomPcAds.map((ad) => (
                    <GoogleAd key={ad} elementId={ad} />
                  ))}
                </>
              }
              spFooterAd={<GoogleAd elementId={pageBottomSpAds} />}
              brandInformation={
                searchBrand ? (
                  <BrandInformation
                    brand={searchBrand}
                    appUrl={`wear2020://wear.jp/search_result/item/?brand_id=${brand?.id}`}
                    appDownloadUrl={ITEM_LIST_FAVORITE_BRAND_ADJUST_URL} //TODO:ショップ一覧のAdjustUrlをいただいたら差し替える
                    pcAppDownloadDialogImageType="shopList"
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      </main>
    </div>
  )
}
